import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AUTH_EXPIRED_EVENT, ApiError, api } from './api';
import type { User } from './types';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // `undefined` significa que a sessão inicial ainda está sendo verificada.
  const [sessionUser, setSessionUser] = useState<User | null | undefined>(undefined);
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await api<{ user: User }>('/auth/me')).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (me.isSuccess) setSessionUser(me.data);
    else if (me.isError) setSessionUser(null);
  }, [me.data, me.isError, me.isSuccess]);

  const clearPrivateCache = useCallback(() => {
    // Nunca remover `['me']` enquanto o AuthProvider a observa. Fazer `qc.clear()` aqui
    // deixava o provider preso ao valor anterior durante o navigate do login.
    qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
  }, [qc]);

  useEffect(() => {
    const onExpired = () => {
      setSessionUser(null);
      qc.setQueryData(['me'], null);
      clearPrivateCache();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [clearPrivateCache, qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api<{ user: User }>('/auth/login', { method: 'POST', body: { email, password } });
      // Confirma que o navegador realmente persistiu o cookie antes de liberar os guards.
      const { user } = await api<{ user: User }>('/auth/me');
      clearPrivateCache();
      qc.setQueryData(['me'], user);
      setSessionUser(user);
      return user;
    },
    [clearPrivateCache, qc],
  );

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', body: {} });
    } finally {
      clearPrivateCache();
      qc.setQueryData(['me'], null);
      setSessionUser(null);
    }
  }, [clearPrivateCache, qc]);

  const value = useMemo<AuthState>(
    () => ({ user: sessionUser ?? null, loading: sessionUser === undefined, login, logout }),
    [sessionUser, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
